import { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';

/**
 * Shared filter + sort + CSV helpers for the Reports views.
 *
 * Pattern:
 *   const ctrl = useReportTable(rows, { filters, defaultSort });
 *   ctrl.filtered     // rows after filter + sort
 *   ctrl.sortBy       // current column key
 *   ctrl.sortDir      // 'asc' | 'desc'
 *   ctrl.setSort(col) // toggles direction or sets new column
 *
 * Each "filter" is a predicate fn(row) → boolean; rebuild them as the user
 * tweaks the controls. Keep their identities stable with useMemo so React
 * re-runs the filter pass only when controls change.
 */
export interface ReportFilter<T> {
  predicate: (row: T) => boolean;
}

export interface UseReportTableOpts<T> {
  filters: Array<((row: T) => boolean) | undefined | null | false>;
  defaultSort?: { key: keyof T | string; dir: 'asc' | 'desc' };
  sortAccessor?: (row: T, key: string) => unknown;
}

export function useReportTable<T extends Record<string, any>>(
  rows: T[] | undefined,
  opts: UseReportTableOpts<T>,
) {
  const [sortBy, setSortBy] = useState<string>(String(opts.defaultSort?.key ?? ''));
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(opts.defaultSort?.dir ?? 'asc');

  function setSort(key: string) {
    if (key === sortBy) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortBy(key);
      setSortDir('asc');
    }
  }

  const activeFilters = useMemo(
    () => opts.filters.filter((f): f is (row: T) => boolean => typeof f === 'function'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [opts.filters],
  );

  const filtered = useMemo(() => {
    if (!rows) return [] as T[];
    let r = rows;
    for (const f of activeFilters) r = r.filter(f);
    if (sortBy) {
      const accessor = opts.sortAccessor ?? defaultAccessor;
      r = [...r].sort((a, b) => {
        const av = accessor(a, sortBy);
        const bv = accessor(b, sortBy);
        const cmp = compareUnknown(av, bv);
        return sortDir === 'asc' ? cmp : -cmp;
      });
    }
    return r;
  }, [rows, activeFilters, sortBy, sortDir, opts.sortAccessor]);

  return { filtered, sortBy, sortDir, setSort };
}

function defaultAccessor(row: Record<string, any>, key: string): unknown {
  if (key in row) return row[key];
  // Allow dotted keys like 'attendance.attendancePct'.
  return key.split('.').reduce<unknown>((acc, seg) => {
    if (acc == null || typeof acc !== 'object') return undefined;
    return (acc as Record<string, unknown>)[seg];
  }, row);
}

function compareUnknown(a: unknown, b: unknown): number {
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (typeof a === 'boolean' && typeof b === 'boolean') return Number(a) - Number(b);
  return String(a).localeCompare(String(b), undefined, { numeric: true });
}

/**
 * Clickable column header. Highlights when this is the sort key, shows arrow.
 *
 *   <SortTh col="cohortId" current={ctrl.sortBy} dir={ctrl.sortDir} onClick={() => ctrl.setSort('cohortId')}>
 *     Cohort
 *   </SortTh>
 */
export function SortTh({
  col,
  current,
  dir,
  onClick,
  align = 'left',
  children,
}: {
  col: string;
  current: string;
  dir: 'asc' | 'desc';
  onClick: () => void;
  align?: 'left' | 'right' | 'center';
  children: React.ReactNode;
}) {
  const active = col === current;
  return (
    <th className={clsx('px-3 py-2 text-2xs uppercase tracking-wide text-ink-subtle', `text-${align}`)}>
      <button
        type="button"
        onClick={onClick}
        className={clsx(
          'inline-flex items-center gap-1 hover:text-ink',
          active && 'font-bold text-ink',
        )}
      >
        <span>{children}</span>
        <span className="text-2xs opacity-70">
          {active ? (dir === 'asc' ? '▲' : '▼') : '↕'}
        </span>
      </button>
    </th>
  );
}

/**
 * Multi-select chip-row. Used for cohort / service-type / status filters.
 * Click a chip to toggle. Empty selection = "no filter" (everything passes).
 */
export function ChipMultiSelect<V extends string>({
  label,
  options,
  selected,
  onChange,
  formatOption,
}: {
  label: string;
  options: readonly V[];
  selected: V[];
  onChange: (next: V[]) => void;
  formatOption?: (v: V) => string;
}) {
  const toggle = (v: V) =>
    selected.includes(v) ? onChange(selected.filter((x) => x !== v)) : onChange([...selected, v]);
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-2xs font-semibold uppercase tracking-wide text-ink-subtle">
        {label}:
      </span>
      {options.map((opt) => {
        const on = selected.includes(opt);
        return (
          <button
            key={opt}
            type="button"
            onClick={() => toggle(opt)}
            className={
              on
                ? 'rounded-md bg-brand-600 px-2 py-0.5 text-2xs font-semibold uppercase text-white'
                : 'rounded-md bg-surface-muted px-2 py-0.5 text-2xs font-semibold uppercase text-ink-muted hover:bg-line'
            }
          >
            {formatOption ? formatOption(opt) : opt}
          </button>
        );
      })}
      {selected.length > 0 && (
        <button
          type="button"
          onClick={() => onChange([])}
          className="text-2xs text-ink-subtle hover:text-ink"
        >
          clear
        </button>
      )}
    </div>
  );
}

/**
 * Compact dropdown version of ChipMultiSelect. Renders as a single button
 * with the label + selected-count; opens a popover with checkboxes for each
 * option + a "Clear" link. Same `selected/onChange` contract as ChipMultiSelect
 * so we can swap them per call-site.
 *
 *   <ChipDropdown label="Cohort" options={['A','B',...]} selected={cohorts} onChange={setCohorts} />
 *
 * Implementation notes:
 *   - Click outside the popover closes it.
 *   - Escape closes it.
 *   - The trigger button shows `(2)` when 2 items are selected, nothing when 0.
 *   - Filter for long option lists (>8 items): an input at the top filters the
 *     checkbox list.
 */
export function ChipDropdown<V extends string>({
  label,
  options,
  selected,
  onChange,
  formatOption,
}: {
  label: string;
  options: readonly V[];
  selected: V[];
  onChange: (next: V[]) => void;
  formatOption?: (v: V) => string;
}) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Click outside + Escape to close.
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const visible = useMemo(() => {
    const t = filter.trim().toLowerCase();
    if (!t) return options;
    return options.filter((o) =>
      (formatOption ? formatOption(o) : o).toLowerCase().includes(t),
    );
  }, [options, filter, formatOption]);

  const toggle = (v: V) =>
    selected.includes(v) ? onChange(selected.filter((x) => x !== v)) : onChange([...selected, v]);

  const count = selected.length;
  const first = selected[0];
  const triggerLabel =
    count === 0
      ? label
      : count === 1 && first !== undefined
        ? `${label}: ${formatOption ? formatOption(first) : first}`
        : `${label} (${count})`;

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={clsx(
          'flex h-8 items-center gap-1 rounded-md border px-2 text-xs font-medium transition',
          count > 0
            ? 'border-brand-300 bg-brand-50 text-brand-800 hover:bg-brand-100'
            : 'border-line bg-white text-ink-muted hover:bg-surface-muted',
        )}
        aria-expanded={open}
      >
        <span>{triggerLabel}</span>
        <svg
          className={clsx('h-3 w-3 transition-transform', open && 'rotate-180')}
          viewBox="0 0 12 12"
          fill="currentColor"
          aria-hidden
        >
          <path d="M3 4.5l3 3 3-3" stroke="currentColor" strokeWidth="1.4" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div className="absolute left-0 top-full z-30 mt-1 w-60 rounded-md border border-line bg-white p-2 shadow-lg">
          <div className="mb-1 flex items-center justify-between gap-2">
            <span className="text-2xs font-semibold uppercase tracking-wide text-ink-subtle">
              {label}
            </span>
            {count > 0 && (
              <button
                type="button"
                onClick={() => {
                  onChange([]);
                  setFilter('');
                }}
                className="text-2xs text-ink-subtle hover:text-ink"
              >
                clear
              </button>
            )}
          </div>
          {options.length > 8 && (
            <input
              type="text"
              className="input h-7 w-full px-2 py-0 text-xs"
              placeholder="Filter…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
          )}
          <ul className="mt-1 max-h-56 space-y-0.5 overflow-y-auto pr-1">
            {visible.length === 0 ? (
              <li className="px-2 py-1 text-2xs text-ink-subtle">No matches</li>
            ) : (
              visible.map((opt) => {
                const on = selected.includes(opt);
                return (
                  <li key={opt}>
                    <label className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-xs hover:bg-surface-muted">
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={() => toggle(opt)}
                        className="h-3.5 w-3.5 accent-brand-600"
                      />
                      <span className="truncate">{formatOption ? formatOption(opt) : opt}</span>
                    </label>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * CSV download utility. Given headers + an extractor per row, build a CSV
 * blob and trigger a browser download.
 */
export function downloadCsv<T>(opts: {
  filename: string;
  headers: string[];
  rows: T[];
  toRow: (row: T) => Array<string | number | null | undefined>;
}) {
  const csvLines = [opts.headers.map(csvQuote).join(',')];
  for (const r of opts.rows) {
    csvLines.push(opts.toRow(r).map(csvQuote).join(','));
  }
  const blob = new Blob([csvLines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = opts.filename;
  a.click();
  URL.revokeObjectURL(url);
}

function csvQuote(v: string | number | null | undefined): string {
  if (v == null) return '';
  const s = String(v);
  if (/[",\n\r]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
  return s;
}

/** Free-text search filter helper. Case-insensitive substring match across many fields. */
export function buildTextFilter<T>(
  term: string,
  fields: (row: T) => Array<string | null | undefined>,
): ((row: T) => boolean) | undefined {
  const t = term.trim().toLowerCase();
  if (!t) return undefined;
  return (row) =>
    fields(row).some((v) => v != null && String(v).toLowerCase().includes(t));
}
